import _ from 'lodash'
import DeepDiff from 'deep-diff'
import { Address, Address as GenericPostalAddress } from '@/models/generics'
import { CountriesByName, CountryIso, CountryMetadata, IsoAlpha2Dict, IsoAlpha3Dict } from '@/constants/country_iso_dict'
import { countryISOToName, countryNameToISO } from './address'
export const isNumber = (n) => !isNaN(parseFloat(n)) && isFinite(n)

// Check if character is a fraction, e.g. ¼
export const isFractionalChar = (n) => {
  const c = n.charCodeAt()
  return (c >= 188 && c <= 190) || (c >= 8531 && c <= 8542)
}

export const indexFractionalChar = (m) => {
  const a = m.split('')
  let i
  for (i in a) {
    if (isFractionalChar(a[i])) return i
  }
  return false
}


const ADDRESS_DEFAULT_OPTIONS = {}


enum AddressFormat {
  // Default = 'eu', //
  EU = 'eu', // wayward road 123, 12345 omaha, nebraska
  US = 'us' // 123 wayward rd. 12345 omaha, nebraska
}
interface PostalAddressOptions { experimental?: boolean, format?: AddressFormat }

export default class PostalAddress {
  private readonly _raw: Partial<GenericPostalAddress> & { [key: string]: any }

  public options: PostalAddressOptions
  public format: AddressFormat

  // private _address: Partial<GenericPostalAddress>
  public _city: GenericPostalAddress['city']
  public _email: GenericPostalAddress['email']
  public _phone: GenericPostalAddress['phone']
  public _street: GenericPostalAddress['street']
  public _street_number: GenericPostalAddress['street_number']
  public _addition: GenericPostalAddress['addition']
  public _zipcode: GenericPostalAddress['zipcode']
  public _region: GenericPostalAddress['region']
  public _state: GenericPostalAddress['state']
  public _country: GenericPostalAddress['country']
  public _country_iso: GenericPostalAddress['country_iso']
  public _county: GenericPostalAddress['county']
  // public _customer: GenericPostalAddress['customer']
  public _firstname: GenericPostalAddress['firstname']
  public _lastname: GenericPostalAddress['lastname']
  public _fullname: GenericPostalAddress['fullname']
  public _company: GenericPostalAddress['company']
  public _department: GenericPostalAddress['department']
  public _meta: GenericPostalAddress['meta']
  public _document_id: GenericPostalAddress['document_id']

  // COMBAK street_number is debatable as being "required" since some addresses (for example in the UK and France) are "private drives" that do not have an explicit number
  static RequiredKeys = ['street', 'street_number', 'city', 'zipcode', 'country_iso']

  constructor (address: Partial<GenericPostalAddress>, options?: PostalAddressOptions) {
    this._raw = address
    this.set(address)

    this.options = {...ADDRESS_DEFAULT_OPTIONS, ...(options || {})}
    this.format = this.options.format || AddressFormat.EU
  }

  get city () { return this._city }
  get email () { return this._email }
  get phone () { return this._phone }

  get street () { return this._street }
  get street_number () { return parseInt(this._street_number as any) }
  get addition () { return this._addition }

  get zipcode () { return this._zipcode }

  get region () { return this._region }
  get country () { return this._country }
  get country_iso () { return this._country_iso }

  get state () { return this._state }
  get county () { return this._county }
  // get customer () { return this._customer }
  get firstname () { return this._firstname }
  get lastname () { return this._lastname }
  get fullname () { return this._fullname }

  get company () { return this._company }
  get department () { return this._department }
  get document_id () { return this._document_id }

  // returns a formatted address based on the set formatting type
  // TODO: default based on country_iso
  get formatted () {
    switch (this.format) {
      case AddressFormat.US:
        return `${this.street_number || ''}${this.addition || ''} ${this.street || ''}, ${this.zipcode || ''} ${this.city || ''}, ${this.country_iso || ''}`
      case AddressFormat.EU:
      default:
        return `${this.street || ''} ${this.street_number || ''}${this.addition || ''}, ${this.zipcode || ''} ${this.city || ''}, ${this.country_iso || ''}`
    }
  }

  get valid () { return Object.entries(this.validation_state).every(([key, value]) => value) }
  get validation_state () {
    return {
      street: !!this._street,
      street_number: !!this._street_number && !isNaN(parseInt(this._street_number as any || '')),
      addition: !this._addition || (this._addition && this._addition.length < 24),
      zipcode: !!this._zipcode,
      city: !!this._city,
      country_iso: !!this._country_iso && !!IsoAlpha2Dict[this._country_iso],
      email: !this._email || (this._email && this._email.length < 256),
      phone: !this._phone || (this._phone && this._phone.length < 256)
    }
  }

  get meta () { return this._meta || {} }

  get value () {
    return {
      city: this.city,
      email: this.email,
      phone: this.phone,
      street: this.street,
      street_number: this.street_number,
      addition: this.addition,
      zipcode: this.zipcode,
      region: this.region,
      state: this.state,
      country: this.country,
      country_iso: this.country_iso,
      county: this.county,
      // customer: this.customer,
      firstname: this.firstname,
      lastname: this.lastname,
      fullname: this.fullname,
      company: this.company,
      department: this.department,
      document_id: this.document_id,
      formatted: this.formatted,
      meta: this.meta
    }
  }

  set (value: Record<string, any>) {
    // TODO: smarter handling per updated key..., normalize simply runs every validation..
    const parsed = this.parse(value)

    this._email = parsed.email || this._email
    this._phone = parsed.phone || this._phone

    this._street = parsed.street || this._street
    this._street_number = parsed.street_number || this._street_number
    this._addition = parsed.addition || this._addition
    this._zipcode = parsed.zipcode || this._zipcode
    this._city = parsed.city || this._city
    this._region = parsed.region || this._region
    this._state = parsed.state || this._state

    const countryEntry = PostalAddress.lookupCountry(parsed.country || this._country || '', parsed.country_iso || this._country_iso || '')
    this._country = countryEntry?.name || this._country
    this._country_iso = countryEntry?.iso.alpha2 || this._country_iso
    this._county = parsed.county || this._county

    this._company = parsed.company || this._company
    this._department = parsed.department || this._department

    this._meta = parsed.meta || this._meta
    this._document_id = parsed.document_id || this._document_id

    // @deprecated
    this._firstname = parsed.firstname || this._firstname
    this._lastname = parsed.lastname || this._lastname
    this._fullname = parsed.fullname || this._fullname
  }

  parse (address: Partial<GenericPostalAddress> & { [key: string]: any }): Partial<GenericPostalAddress> {
    if (this.options?.experimental) return this.parseAddress(address)
    else return this.legacyParser(address)
  }

  /**
   * @description verifies address fields, and uses libpostal to parse the full address, combining address parts in all relevant ways, calculating a likelyness-score for each address part, and fill / correct fields
   */
  parseAddress (address: Partial<GenericPostalAddress> & { [key: string]: any }): GenericPostalAddress {
    return address
  }

  private legacyParser (addressData: Partial<GenericPostalAddress> & { [key: string]: any }): GenericPostalAddress {
    const parsedAddress = addressData
    //  extract street & number (& optionally addition)
    if (!parsedAddress.street_number && (parsedAddress.streetnumber || parsedAddress.streetNumber || parsedAddress.number)) parsedAddress.street_number = parsedAddress.streetNumber || parsedAddress.streetnumber || parsedAddress.number

    if (!parsedAddress.street || !parsedAddress.street_number) {
      const addressString = parsedAddress.street || addressData.address || addressData.address1 || addressData.address2 || addressData.address3 || ''
      // eslint-disable-next-line no-useless-escape
      const rx = /^(\d*[\p{L}\d '\/\\\-\.]+)[,\s]+(\d+)\s*([\p{L} \d\-\/'"\(\)]*)$/iu

      const match = addressString.match(rx)
      if (match) {
        const [src, street, street_number, addition] = match
        parsedAddress.street = street || parsedAddress.street

        parsedAddress.street_number = street_number || parsedAddress.streetNumber || parsedAddress.streetnumber
        parsedAddress.addition = addition || parsedAddress.addition
      } else {
        // if the regex cannot match, use the good ol' splitAdresss which is more suited to US adresses and the like
        const { number, addition, space, street } = PostalAddress.utils.legacy.split(addressString)
        parsedAddress.street = street || parsedAddress.street
        parsedAddress.street_number = number || parsedAddress.street_number || parsedAddress.streetNumber || parsedAddress.streetnumber
        parsedAddress.addition = addition || parsedAddress.addition
      }
    }

    // see if we can look up the city based on the zipcode
    // if (!parsedAddress.city)

    // convert country / country_iso
    if (!parsedAddress.country_iso) parsedAddress.country_iso = PostalAddress.utils.legacy.country.nameToIso(addressData.country)
    if (!parsedAddress.country) parsedAddress.country = PostalAddress.utils.legacy.country.isoToName(addressData.country_iso)

    // set formatted address string
    if (!parsedAddress.formatted) parsedAddress.formatted = `${parsedAddress.street} ${parsedAddress.street_number}, ${parsedAddress.zipcode} ${parsedAddress.city}, ${parsedAddress.country}`

    // return all fields in the original object, with the necessary corrections
    return parsedAddress
  }

  static get utils () {
    return {
      split: null,
      country: { isoToName: null, nameToIso: null },
      legacy: {
        country: { isoToName: countryISOToName, nameToIso: countryNameToISO},
        split: PostalAddress.roughSplit
      },
      diffToValue: (diffArray: Array<DeepDiff.Diff<Address, Address>>, rootKey: string) => {
        return diffArray.reduce((address, diff) => {
          if (diff?.kind === 'E' || diff?.kind === 'N')
            _.set(
              address,
              (rootKey && diff.path?.[0] === rootKey) ? diff.path?.slice(1) : diff.path as any,
              diff.rhs
            )

          return address
        }, {})
      }
    }
  }

  static lookupCountry (country: string, country_iso: string): CountryMetadata | undefined {
    return IsoAlpha2Dict[country_iso as CountryIso] as CountryMetadata ||
      IsoAlpha3Dict[country_iso as CountryIso] ||
      CountriesByName[String(country).trim().toLowerCase().split(' ').join('-')] as CountryMetadata
  }

  static roughSplit (address) {
    if (!address) return {}
    const parts = address.split(',') // split if address is full formatted address

    // always take the first part, assuming the address is either only the
    // street + street-number or a full formatted address in which case,
    // by convension, the street + street-number should always come first
    const x = parts[0]
    const a = x.trim().split(' ')
    let number
    let addition

    if (a.length <= 1) {
      return { number: '', addition: '', space: '', street: a.join('') }
    }

    if (isNumber(a[0].substr(0, 1)) || isFractionalChar(a[0].substr(0, 1))) {
      number = a.shift()
    }
    else if (isNumber(a[a.length - 1].substr(0, 1)) || isFractionalChar(a[a.length - 1].substr(0, 1))) {
      number = a.pop()
    }
    else if (isNumber(parseInt(a[0].substr(0, 1)))) {
      const dirtyNum = a.shift()
      number = parseInt(dirtyNum)
      addition = dirtyNum.replace(/[0-9]/g, '')
    }
    else if (isNumber(parseInt(a[a.length - 1].substr(0, 1)))) {
      const dirtyNum = a.pop()
      number = parseInt(dirtyNum)
      addition = dirtyNum.replace(/[0-9]/g, '')
    }
    else {
      return { number: '', addition: '', space: '', street: x.trim() }
    }
    if (/[0-9]\/[0-9]/.exec(a[0]) || indexFractionalChar(a[0]) !== false) {
      number += ` ${a.shift()}`
    }
    return { number, addition, space: ' ', street: a.join(' ') }
  }
}
